import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';

//TODO Code Splitting
//TODO Lazy Loading - Done
// Only import core components that are needed for initial rendering
import Navigation from './components/Navigation';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleRoute from './components/auth/RoleRoute';


// Loading component for Suspense fallback
const LoadingComponent = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    <p className="ml-3 text-gray-700">Loading...</p>
  </div>
);

// Lazily load all components by category
// Landing and Auth
const LandingPage = lazy(() => import('./components/LandingPage'));
const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/auth/Register'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const GenerateReferralCode = lazy(() => import('./components/GenerateReferralCode'));

// Projects
const Projects = lazy(() => import('./components/Projects'));
const ProjectDetail = lazy(() => import('./components/ProjectDetail'));
const SprintAnalytics = lazy(() => import('./components/SprintAnalytics'));
const SprintAnalyticsPage = lazy(() => import('./components/SprintAnalyticsPage'));
const Reports = lazy(() => import('./components/Reports'));
const Articles = lazy(() => import('./components/Articles'));
const Prospecting = lazy(() => import('./components/Prospecting'));

// Admin
const Admin = lazy(() => import('./components/admin'));
const AdminCognito = lazy(() => import('./components/AdminCognito'));
const Login_Cognito = lazy(() => import('./components/auth/Login_Cognito'));
const Register_Cognito = lazy(() => import('./components/auth/Register_Cognito'));

const InfrastructureDashboard = lazy(() => import('./components/InfrastructureDashboard'));
const RoleTest = lazy(() => import('./components/RoleTest'));
const SubscriptionPlans = lazy(() => import('./components/SubscriptionPlans'));
const ArticleCreator = lazy(() => import('./components/ArticleCreator'));
const ProductPackageCreator = lazy(() => import('./components/ProductPackageCreator.jsx'));
const MilestoneDashboard = lazy(() => import('./components/milestones/MilestoneDashboard'));

// Cohorts
const CohortCreator = lazy(() => import('./components/cohorts/CohortCreator'));
const CohortSelection = lazy(() => import('./components/cohorts/CohortSelection'));

// Calculator Components
const Calculators = lazy(() => import('./components/Calculators'));
const BudgetCalculator = lazy(() => import('./components/calculators/BudgetCalculator'));
const CreditAssessment = lazy(() => import('./components/calculators/CreditAssessment'));
const EmergencyFundCalculator = lazy(() => import('./components/calculators/EmergencyFundSavingsCalculator'));
const RenovationsCalculator = lazy(() => import('./components/calculators/RenovationsCalculator'));
const RetirementCalculator = lazy(() => import('./components/calculators/RetirementCalculator'));
const TrueCostOfCreditCalculator = lazy(() => import('./components/calculators/TrueCostOfCreditCalculator'));
const InterestRatesCompare = lazy(() => import('./components/calculators/InterestRatesCompare'));

// AI
const AIAgentBusinessPlanner = lazy(() => import('./components/ai/AIAgentBusinessPlanner'));
const AIMonetizationPlanner = lazy(() => import('./components/ai/AIMonetizationPlanner'));
const AIProtectionPlanner = lazy(() => import('./components/ai/AIProtectionPlanner'));
const AITwinIntroPlanner = lazy(() => import('./components/ai/AITwinIntroPlanner'));
const AIROIEstimator = lazy(() => import('./components/ai/AIROIEstimator'));
const AITwinBusinessForm = lazy(() => import('./components/ai/lead_forms/AITwinBusinessForm'));
const AITwinCareerTransitionForm = lazy(() => import('./components/ai/lead_forms/AITwinCareerTransitionForm'));
const AITwinCommunityForm = lazy(() => import('./components/ai/lead_forms/AITwinCommunityForm'));
const AITwinHRPlanner = lazy(() => import('./components/ai/AITwinHRPlanner'));
const AITwinMarketingPlanner = lazy(() => import('./components/ai/AITwinMarketingPlanner'));
const AIProspecting = lazy(() => import('./components/ai/AIProspecting'));
const AIHRROIEstimator = lazy(() => import('./components/ai/AIHRROIEstimator'));
const AITwinImpactDashboard = lazy(() => import('./components/ai/AITwinImpactDashboard'));
const AIBrandingMarketingTeam = lazy(() => import('./components/ai/agents/AIBrandingMarketingTeam'));
const AIValuation = lazy(() => import('./components/ai/AIValuation'));
const AIBusinessAdvance = lazy(() => import('./components/ai/AIBusinessAdvance') );

//const AgentTestComponent = lazy(() => import('./components/ai/agents/AgentTestComponent'));

// Flow Diagrams
const TreasuryBondFlowDiagram = lazy(() => import('./components/calculators/TreasuryBondFlowDiagram'));
const EquityFlowDiagram = lazy(() => import('./components/calculators/EquityFlowDiagram'));
const CorpBondsFlowDiagram = lazy(() => import('./components/calculators/CorpBondsFlowDiagram'));
const MBSFlowDiagram = lazy(() => import('./components/calculators/MBSFlowDiagram'));
const OptionsFlowDiagram = lazy(() => import('./components/calculators/OptionsFlowDiagram'));
const FuturesFlowDiagram = lazy(() => import('./components/calculators/FuturesFlowDiagram'));
const ForwardsFlowDiagram = lazy(() => import('./components/calculators/ForwardsFlowDiagram'));
const MoneyMarketFundsFlowDiagram = lazy(() => import('./components/calculators/MoneyMarketFundsFlowDiagram'));
const ETFsFlowDiagram = lazy(() => import('./components/calculators/ETFsFlowDiagram'));
const MutualFundsFlowDiagram = lazy(() => import('./components/calculators/MutualFundsFlowDiagram'));

// Tutorials
const React_NoviceTutorial = lazy(() => import('./components/training/React_Novice'));

// Training Components
const TimeValueMoneyTraining = lazy(() => import('./components/training/TimeValueOfMoneyTraining'));
const BudgetTraining = lazy(() => import('./components/training/BudgetingTraining'));
const DebtManagementTraining = lazy(() => import('./components/training/DebtManagementTraining'));
const CapitalStructureTraining = lazy(() => import('./components/training/CapitalStructureTraining'));
const CorporateValuationTraining = lazy(() => import('./components/training/CorporateValuationTraining'));
const CorporateBondTraining = lazy(() => import('./components/training/CorporateBondTraining'));
const MergersAcquisitionsTraining = lazy(() => import('./components/training/MergersAcquisitionsTraining'));
const RealEstateFundamentalsTraining = lazy(() => import('./components/training/RealEstateFundamentals'));
const RealEstateResidentialTraining = lazy(() => import('./components/training/RealEstateResidentialTraining'));
const RealEstateCommercialTraining = lazy(() => import('./components/training/RealEstateCommercialTraining'));
const REDevelopmentConstructionTraining = lazy(() => import('./components/training/REDevelopmentConstructionTraining'));
const REPropertyManagementTraining = lazy(() => import('./components/training/REPropertyManagementTraining'));
const RealEstateFinanceTraining = lazy(() => import('./components/training/RealEstateFinanceTraining'));
const TreasuryYieldTraining = lazy(() => import('./components/training/TreasuryYieldTraining'));
const TreasuriesCredit = lazy(() => import('./components/training/TreasuriesCredit'));
const TreasuriesHousing = lazy(() => import('./components/training/TreasuriesHousing'));

// Tools 
const FAQ = lazy (() => import('./components/tools/FAQ'));


// HNW High Net Worth
const OptionsModeling = lazy(() => import('./components/hnw/OptionsModeling'));
const EquitiesDashboard = lazy(() => import('./components/hnw/EquitiesDashboard'));
const EquitiesEarningsCalls = lazy(() => import('./components/hnw/EquitiesEarningsCalls'));
const InsiderTradingModeling = lazy(() => import('./components/hnw/InsiderTradingModeling'));
const RiskToleranceAssessment = lazy(() => import('./components/hnw/RiskToleranceAssessment'));
const HNWIntake = lazy(() => import('./components/hnw/HNWIntake'));
const EquitiesFXTechnicals = lazy(() => import('./components/hnw/EquitiesFXTechnicals'));
const FXModeling = lazy(() => import('./components/hnw/FXModeling'));
const MacrosModeling = lazy(() => import('./components/hnw/MacrosModeling'));
const CommoditiesModeling = lazy(() => import ('./components/hnw/CommoditiesModeling'));
const MarketMoversMomentum = lazy(() => import('./components/hnw/MarketMoversMomentum'));
const RealEstateModeler = lazy(() => import('./components/hnw/RealEstateModeler'));
const RealEstateAnalysis = lazy(() => import('./components/hnw/RealEstateAnalysis'));
const SectorAnalysisModeling = lazy(() => import('./components/hnw/SectorAnalysisModeling'));
const StratAdvise = lazy(() => import('./components/hnw/StratAdvise'));


// Portfolio
const PortfolioHoldings = lazy(() => import('./components/portfolio/PortfolioHoldings'));
const PortfolioAnalysis = lazy(() => import('./components/portfolio/PortfolioAnalysis'));
const PortfolioScenarios = lazy(() => import('./components/portfolio/PortfolioScenarios'));
const PortfolioPerformance = lazy(() => import('./components/portfolio/PortfolioPerformance'));
const PortfolioGlobalMacroDashboard = lazy(() => import('./components/portfolio/PortfolioGlobalMacroDashboard'));
const HistoricalCurrencyRates = lazy(() => import('./components/portfolio/HistoricalCurrencyRates'));
const DiscourseTracker = lazy(() => import('./components/discourse/DiscourseTracker'));


// PnL Models
const CapTables = lazy(() => import('./components/pnlmodels/CapTables.js'));
const FamilyLegacyPnL = lazy(() => import('./components/pnlmodels/FamilyLegacyPnL'));
const InvestmentMgmtServicesPnL = lazy(() => import('./components/pnlmodels/InvestmentMgmtServicesPnL'));
const StrategicAdvisoryPnL = lazy(() => import('./components/pnlmodels/StrategicAdvisoryPnL'));
const PortfolioRiskPnL = lazy(() => import('./components/pnlmodels/PortfolioRiskPnL'));
const ResearchAnalysisPnL = lazy(() => import('./components/pnlmodels/ResearchAnalysisPnL'));
const TechnologyServicesPnL = lazy(() => import('./components/pnlmodels/TechnologyServicesPnL'));
const PnLDashboard = lazy(() => import('./components/pnlmodels/PnLDashboard'));

// NP
const SecureNPPlanner = lazy(() => import('./components/np/SecureNPPlanner'));
const NPSecurityLeadForm = lazy(() => import('./components/np/leadforms/NPSecurityLeadForm'));
const NPDataProductPage = lazy(() => import('./components/np/DataProductPage'));
const NonProfitEconomicsPlanner = lazy(() => import('./components/np/NonProfitEconomicsPlanner'));
const NPEconomicsDataProductPage = lazy(() => import('./components/np/NPEconomicsDataProductPage'));
const NPProspecting = lazy(() => import('./components/np/NPProspecting'));

// Const statements for impact_products
const DataSecurity = lazy(() => import('./components/np/impact_products/DataSecurity'));
const DonorDemographics = lazy(() => import('./components/np/impact_products/DonorDemographics'));
const EconomicCycles = lazy(() => import('./components/np/impact_products/EconomicCycles'));
const EconomicsUtils = lazy(() => import('./components/np/impact_products/EconomicsUtils'));
const EmailSecurity = lazy(() => import('./components/np/impact_products/EmailSecurity'));
const FileSecurity = lazy(() => import('./components/np/impact_products/FileSecurity'));
const FinancialSecurity = lazy(() => import('./components/np/impact_products/FinancialSecurity'));
const FundraisingStrategies = lazy(() => import('./components/np/impact_products/FundraisingStrategies'));
const GovernmentFunding = lazy(() => import('./components/np/impact_products/GovernmentFunding'));
const InformationSecurity = lazy(() => import('./components/np/impact_products/InformationSecurity'));
const OperationalSecurity = lazy(() => import('./components/np/impact_products/OperationalSecurity'));
const ResiliencePlaybook = lazy(() => import('./components/np/impact_products/ResiliencePlaybook'));



// PRV
const FoodTruckPlanner = lazy(() => import('./components/prv/FoodTruckPlanner'));
const RetirementCalcPlanner = lazy(() => import('./components/prv/RetirementCalcPlanner'));
const BudgetPlanner = lazy(() => import('./components/prv/BudgetPlanner'));
const CreditAssessmentPlanner = lazy(() => import('./components/prv/CreditAssessmentPlanner'));
const UnderstandingTaxesPlanner = lazy(() => import('./components/prv/UnderstandingTaxesPlanner'));
const DataProductPage = lazy(() => import('./components/prv/DataProductPage'));
const PRVProspecting = lazy(() => import('./components/prv/PRVProspecting'));
const PRVEcosystemWheelGenerator = lazy(() => import('./components/prv/PRVEcosystemWheelGenerator'))


const FoodTruck = lazy(() => import('./components/prv/foodtruck/FoodTruck'));
const FoodTruckMarketing = lazy(() => import('./components/prv/foodtruck/FoodTruckMarketing'));
const FoodTruckEquipment = lazy(() => import('./components/prv/foodtruck/FoodTruckEquipment'));
const FoodTruckInsurance = lazy(() => import('./components/prv/foodtruck/FoodTruckInsurance'));
const FoodTruckWorkers = lazy(() => import('./components/prv/foodtruck/FoodTruckWorkers'));
const FoodTruckStartupCosts = lazy(() => import('./components/prv/foodtruck/FoodTruckStartupCosts'));
const FoodTruckBankCreditAccounts = lazy(() => import('./components/prv/foodtruck/FoodTruckBankCreditAccounts'));


// HOA
const HOAFinancials = lazy(() => import('./components/hoa/HOAFinancials'));
const HOAFlowDiagram = lazy(() => import('./components/hoa/HOAFlowDiagram'));
const HOATuneupFlowDiagram = lazy(() => import('./components/hoa/HOATuneUPFlowDiagram'));
const HOAOperatingDashboard = lazy(() => import('./components/hoa/HOAOperatingDashboard'));
const HOAAssetShield = lazy(() => import('./components/hoa/HOAAssetShield'));
const PremiumHOATuneup = lazy(() => import('./components/hoa/PremiumHOATuneup'));
const PremiumHOATuneupDataManager = lazy(() => import('./components/hoa/PremiumHOATuneupDataManager'));
const PremiumHOATuneupAssessment = lazy(() => import('./components/hoa/PremiumHOATuneupAssessment'));
const HOAComplianceSync = lazy(() => import('./components/hoa/HOAComplianceSync'));
const HOAFinancePulse = lazy(() => import('./components/hoa/HOAFinancePulse'));
const HOAFinanceDataManager = lazy(() => import('./components/hoa/HOAFinanceDataManager'));
const HOACommunityConnect = lazy(() => import('./components/hoa/HOACommunityConnect'));
const ReferralDashboard = lazy(() => import('./components/hoa/ReferralDashboard'));
const ProcessManager = lazy(() => import('./components/ProcessManager'));
const HOAROIEstimator = lazy(() => import('./components/hoa/HOAROIEstimator'));
const HARIS = lazy(() => import('./components/hoa/HARIS'));
const HOAMembership = lazy(() => import('./components/hoa/HOAMembership'));
const HOATransform = lazy(() => import('./components/hoa/HOATransform'));
const HOAOurHome = lazy(() => import('./components/hoa/HOAOurHome'));
const OurHomeVendorMgmt = lazy(() => import('./components/hoa/OurHomeVendorMgmt.js'));


// OH - OurHome 
const OHOurHome = lazy(() => import('./components/oh/OHOurHome'));
const OHOurCar = lazy(() => import('./components/oh/OHOurCar'));
const OHOurHomeVendorSetup = lazy(() => import('./components/oh/OHOurHomeVendorSetup'));
const OHOurCarVendorSetup = lazy(() => import('./components/oh/OHOurCarVendorSetup'));
const OHVendorSubscribe = lazy(() => import('./components/oh/OHVendorSubscribe'));
const OHOurHomeSubscribe = lazy(() => import('./components/oh/OHOurHomeSubscribe'));
const OHOurCarSubscribe = lazy(() => import('./components/oh/OHOurCarSubscribe'));
const OHOurHomeAssetMaintainModeler  = lazy(() => import('./components/oh/OHHomeAssetMaintainModeler.js'));
const OHREAgentSubscribe = lazy(() => import('./components/oh/OHREAgentSubscribe.js'));
const ZipLocatorWidget = lazy(() =>import('./components/ZipLocator.js'));
const OhmUserProfile = lazy(() => import('./components/oh/OhmUserProfile.js'));
const OhmUserProfileRE = lazy(() => import('./components/oh/OhmUserProfileRE.js'));
const OHOurCarAssetMaintainModeler  = lazy(() => import('./components/oh/OHCarAssetMaintainModeler.js'));
const CarSalesProfitability = lazy(() => import('./components/oh/CarSalesProfitability.js'));
const FreeTurnoverCostReport = lazy(() => import('./components/oh/FreeTurnoverCostReport'));
const RooftopOptimize = lazy(() => import('./components/oh/OHRooftopOptimize.jsx'));
const OHBrokerageDashboard = lazy(() => import('./components/oh/BrokerageDashboard.js'));



/* to import and have a default module}
const FinanceDataProvider = lazy(() => 
  import('./components/hoa/FinanceDataContext').then(module => ({
    default: module.FinanceDataProvider
  }))
);*/

// RE Real Estate Familiy Office
const OrgStructureVisualization = lazy(() => import('./components/re/OrgStructureVisualization'));
const OfferingBuilder = lazy(() => import('./components/re/OfferingBuilder'));
const Transactions = lazy(() => import('./components/re/Transactions'));
const TransactionRoles = lazy(() => import('./components/re/TransactionRoles'));
const Checklists = lazy(() => import('./components/re/Checklists'));
const TransactionProfitability = lazy(() => import('./components/re/TransactionProfitability'));
const VendorMgmt = lazy(() => import('./components/re/VendorMgmt'));
const REDashboard = lazy(() => import('./components/re/REDashboard'));
const PipelineDashboard = lazy(() => import('./components/re/PipelineDashboard'));
const TeamFeedback = lazy(() => import('./components/re/TeamFeedback'));
const PipelineIntake = lazy(() => import('./components/re/PipelineIntake'));
const TrainingModule = lazy(() => import('./components/re/TrainingModule'));
const RETransactionTracker = lazy(() => import('./components/re/RETransactionTracker'));
const ProposalBuilder = lazy(() => import('./components/re/ProposalBuilder'));
const Comparables = lazy(() => import('./components/re/Comparables'));
const REListings = lazy(() => import('./components/re/REListings'));
const RETeam = lazy(() => import('./components/re/RETeam'));
const RETraining = lazy(() => import('./components/re/RETraining'));
const REIntegrationPoints = lazy(() => import('./components/re/REIntegrationPoints'));
const REPlatformSecurity = lazy(() => import('./components/re/REPlatformSecurity'));
const ROIEstimator = lazy(() => import('./components/re/ROIEstimator'));
const PropertyShare = lazy(() => import('./components/re/PropertyShare'));
const REOnboardingProcess = lazy(() => import('./components/re/REOnboardingProcess'));
const REProspecting = lazy(() => import('./components/re/REProspecting'));
const REAgentPerformance = lazy(() => import('./components/re/REAgentPerformance'));
const LuxuryAcquisitionEval = lazy(() => import('./components/re/LuxuryAcquisitionEval'));

// MMG
const MMGExam = lazy(() => import('./components/mmg/MMGExam'));

// Wrapper component to handle conditional rendering of navigation
const AppLayout = ({ children }) => {
  const { user } = useAuth();
  return (
    <>
      {user && <Navigation />}
      <main className={user ? 'container mx-auto px-4 py-8' : ''}>
        {children}
        {/* Footer */}
        <footer className="bg-gray-700 text-white p-3 text-center text-sm mt-6 rounded-lg">
          <p>© {new Date().getFullYear()} AiFinCoach Platform | All Rights Reserved</p>
        </footer>
      </main>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Suspense fallback={<LoadingComponent />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={
                <AppLayout>
                  <LandingPage />
                </AppLayout>
              } />
              {/* Public lead magnet — Playbook V8 cold-email CTA. NOT ProtectedRoute. */}
              <Route path="/oh/free-turnover-cost-report" element={
                <AppLayout>
                  <FreeTurnoverCostReport />
                </AppLayout>
              } />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Admin />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/zip-locator-widget" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ZipLocatorWidget />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/prospecting" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Prospecting />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin-cognito" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdminCognito />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/login-cognito" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Login_Cognito />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/register-cognito" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Register_Cognito />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/generate-referral-code" element={
                <ProtectedRoute>
                  <AppLayout>
                    <GenerateReferralCode />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/product-packages" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProductPackageCreator />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/milestones/milestone-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <MilestoneDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
               <Route path="/infrastructure-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <InfrastructureDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/login" element={
                <AppLayout>
                  <Login />
                </AppLayout>
              } />
              <Route path="/register" element={
                <AppLayout>
                  <Register />
                </AppLayout>
              } />

              {/* Protected routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <REDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/process-manager" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProcessManager />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/risk-tolerance-assessment" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RiskToleranceAssessment />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/strat-advise" element={
                <ProtectedRoute>
                  <AppLayout>
                    <StratAdvise />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/tools/faq" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FAQ />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/projects" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Projects />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/articles" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Articles />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/articles/create" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ArticleCreator />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/projects/:projectId" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProjectDetail />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/projects/:projectId/sprintanalytics" element={
                <ProtectedRoute>
                  <AppLayout>
                    <SprintAnalyticsPage />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Reports />
                  </AppLayout>
                </ProtectedRoute>
              } />

              {/* Portfolio Routes */}
              <Route path="/portfolio/currentholdings" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PortfolioHoldings />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/portfolio/analysis" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PortfolioAnalysis />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/portfolio/performance" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PortfolioPerformance />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/portfolio/scenarios" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PortfolioScenarios />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/portfolio/globalmacrodashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PortfolioGlobalMacroDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />

              {/* Calculator Routes */}
              <Route path="/calculators" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Calculators />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/budget" element={
                <ProtectedRoute>
                  <AppLayout>
                    <BudgetCalculator />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/credit-assessment" element={
                <ProtectedRoute>
                  <AppLayout>
                    <CreditAssessment />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/emergency-fund" element={
                <ProtectedRoute>
                  <AppLayout>
                    <EmergencyFundCalculator />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/renovations" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RenovationsCalculator />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/retirement" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RetirementCalculator />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/true-cost-credit" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TrueCostOfCreditCalculator />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/time-value-of-money" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TimeValueMoneyTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/budget" element={
                <ProtectedRoute>
                  <AppLayout>
                    <BudgetTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/debt-management" element={
                <ProtectedRoute>
                  <AppLayout>
                    <DebtManagementTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/capital-structure" element={
                <ProtectedRoute>
                  <AppLayout>
                    <CapitalStructureTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/corporate-valuation" element={
                <ProtectedRoute>
                  <AppLayout>
                    <CorporateValuationTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/corporate-bond" element={
                <ProtectedRoute>
                  <AppLayout>
                    <CorporateBondTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/mergers-acquisitions" element={
                <ProtectedRoute>
                  <AppLayout>
                    <MergersAcquisitionsTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/real-estate-fundamentals" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RealEstateFundamentalsTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/real-estate-residential" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RealEstateResidentialTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/real-estate-commercial" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RealEstateCommercialTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/re-development-construction" element={
                <ProtectedRoute>
                  <AppLayout>
                    <REDevelopmentConstructionTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/re-property-management" element={
                <ProtectedRoute>
                  <AppLayout>
                    <REPropertyManagementTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/real-estate-finance" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RealEstateFinanceTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/subscriptionPlans" element={
                <ProtectedRoute>
                  <AppLayout>
                    <SubscriptionPlans />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/orgstructurevisualization" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OrgStructureVisualization />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/re-agent-performance" element={
                <ProtectedRoute>
                  <AppLayout>
                    <REAgentPerformance />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/re/property-share" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PropertyShare />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/luxury-acquisition-eval" element={
                <ProtectedRoute>
                  <AppLayout>
                    <LuxuryAcquisitionEval />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/re-onboarding-process" element={
                <ProtectedRoute>
                  <AppLayout>
                    <REOnboardingProcess />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/re-integration-points" element={
                <ProtectedRoute>
                  <AppLayout>
                    <REIntegrationPoints />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/offering-builder" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OfferingBuilder />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/re-platform-security" element={
                <ProtectedRoute>
                  <AppLayout>
                    <REPlatformSecurity />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/transactions" requiredRoles="admin" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Transactions />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/transaction-roles" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TransactionRoles />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/transaction-profitability" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TransactionProfitability />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/re/re-listings" element={
                <ProtectedRoute>
                  <AppLayout>
                    <REListings />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/re-team" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RETeam />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/re/re-prospecting" element={
                  <ProtectedRoute>
                    <AppLayout>
                      <REProspecting />
                    </AppLayout>
                  </ProtectedRoute>
                } />
              <Route path="/role-test" element={
                <RoleRoute>
                  <AppLayout>
                    <RoleTest />
                  </AppLayout>
                </RoleRoute>
              } />
              <Route path="/re/vendor-mgmt" element={
                <RoleRoute>
                  <AppLayout>
                    <VendorMgmt />
                  </AppLayout>
                </RoleRoute>
              } />
              <Route path="/re/team-feedback" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TeamFeedback />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/checklists" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Checklists />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/pipeline-intake" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PipelineIntake />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/re-transaction-tracker" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RETransactionTracker />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/training-module" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TrainingModule />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/re-training" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RETraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/re-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <REDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/pipeline-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PipelineDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/proposal-builder" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProposalBuilder />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/comparables" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Comparables />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/re/roi-estimator" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ROIEstimator />
                  </AppLayout>
                </ProtectedRoute>
              } />
            
              <Route path="/cohorts/cohort-creator" element={
                <ProtectedRoute>
                  <AppLayout>
                    <CohortCreator />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/cohorts/cohort-selection" element={
                <ProtectedRoute>
                  <AppLayout>
                    <CohortSelection />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/pnl/pnl-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PnLDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/pnl/cap-tables" element={
                <ProtectedRoute>
                  <AppLayout>
                    <CapTables />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/pnl/family-legacy" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FamilyLegacyPnL />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/pnl/investment-mgmt" element={
                <ProtectedRoute>
                  <AppLayout>
                    <InvestmentMgmtServicesPnL />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/pnl/strategic-advisory" element={
                <ProtectedRoute>
                  <AppLayout>
                    <StrategicAdvisoryPnL />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/pnl/portfolio-risk" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PortfolioRiskPnL />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/pnl/research-analysis" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ResearchAnalysisPnL />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/pnl/technology-services" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TechnologyServicesPnL />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/interest-rates-compare" element={
                <ProtectedRoute>
                  <AppLayout>
                    <InterestRatesCompare />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/treasury-bond-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TreasuryBondFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/corp-bonds-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <CorpBondsFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/equity-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <EquityFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/mbs-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <MBSFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/options-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OptionsFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/forwards-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ForwardsFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/futures-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FuturesFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/mutual-funds-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <MutualFundsFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/money-market-funds-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <MoneyMarketFundsFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/calculators/etfs-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ETFsFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/treasury-yield-training" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TreasuryYieldTraining />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/treasuries-credit" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TreasuriesCredit />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/training/treasuries-housing" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TreasuriesHousing />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/hnw/hnw-intake" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HNWIntake />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/equities-fx-technicals" element={
                <ProtectedRoute>
                  <AppLayout>
                    <EquitiesFXTechnicals />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/insider-trading-modeling" element={
                <ProtectedRoute>
                  <AppLayout>
                    <InsiderTradingModeling />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/market-movers-momentum-modeling" element={
                <ProtectedRoute>
                  <AppLayout>
                    <MarketMoversMomentum />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/sector-analysis-modeling" element={
                <ProtectedRoute>
                  <AppLayout>
                    <SectorAnalysisModeling />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/real-estate-modeler" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RealEstateModeler />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/real-estate-analayis" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RealEstateAnalysis />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/options-modeling" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OptionsModeling />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/fx-modeling" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FXModeling />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/macros-modeling" element={
                <ProtectedRoute>
                  <AppLayout>
                    <MacrosModeling />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/commodities-modeling" element={
                <ProtectedRoute>
                  <AppLayout>
                    <CommoditiesModeling />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/equities-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <EquitiesDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/equities-earnings-calls" element={
                <ProtectedRoute>
                  <AppLayout>
                    <EquitiesEarningsCalls />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hnw/discourse-tracker" element={
                <ProtectedRoute>
                  <AppLayout>
                    <DiscourseTracker />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/ai/ai-prospecting" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIProspecting />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/ai-business-advance" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIBusinessAdvance />
                  </AppLayout>
                </ProtectedRoute>
              } />

              

              <Route path="/ai/ai-hr-roi-estimator" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIHRROIEstimator/>
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/ai-marketing-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AITwinMarketingPlanner/>
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/ai-valuation" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIValuation/>
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/aitwin-business-form" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AITwinBusinessForm />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/aitwin-career-transition-leadform" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AITwinCareerTransitionForm />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/agent-test-component" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AITwinCareerTransitionForm />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/aitwin-community-leadform" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AITwinCommunityForm />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/ai-agent-business-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIAgentBusinessPlanner />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/ai-intro-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AITwinIntroPlanner/>
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/ai-hr-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AITwinHRPlanner/>
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/ai-roi-estimator" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIROIEstimator />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/ai-branding-marketing-team" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIBrandingMarketingTeam />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/ai-protection-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIProtectionPlanner />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/ai-monetization-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIMonetizationPlanner />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai/ai-twin-impact-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <AITwinImpactDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/training/react-novice" element={
                <ProtectedRoute>
                  <AppLayout>
                    <React_NoviceTutorial />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/np/secure-np-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <SecureNPPlanner />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/np/np-economics-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <NonProfitEconomicsPlanner />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/np/np-prospecting" element={
                <ProtectedRoute>
                  <AppLayout>
                    <NPProspecting />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/np/nonprofit-security-lead-form" element={
                <ProtectedRoute>
                  <AppLayout>
                    <NPSecurityLeadForm />
                  </AppLayout>
                </ProtectedRoute>
              } />             
              <Route path="/np/data-product-page" element={
                <ProtectedRoute>
                  <AppLayout>
                    <DataProductPage />
                  </AppLayout>
                </ProtectedRoute>
              } /> 
              <Route path="/np/np-economics-data-product-page" element={
                <ProtectedRoute>
                  <AppLayout>
                    <NPEconomicsDataProductPage />
                  </AppLayout>
                </ProtectedRoute>
              } /> 
            <Route path="/np/impact-products/data-security" element={
              <ProtectedRoute>
                <AppLayout>
                  <DataSecurity />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/np/impact-products/donor-demographics" element={
              <ProtectedRoute>
                <AppLayout>
                  <DonorDemographics />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/np/impact-products/economic-cycles" element={
              <ProtectedRoute>
                <AppLayout>
                  <EconomicCycles />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/np/impact-products/economics-utils" element={
              <ProtectedRoute>
                <AppLayout>
                  <EconomicsUtils />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/np/impact-products/email-security" element={
              <ProtectedRoute>
                <AppLayout>
                  <EmailSecurity />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/np/impact-products/file-security" element={
              <ProtectedRoute>
                <AppLayout>
                  <FileSecurity />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/np/impact-products/financial-security" element={
              <ProtectedRoute>
                <AppLayout>
                  <FinancialSecurity />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/np/impact-products/fundraising-strategies" element={
              <ProtectedRoute>
                <AppLayout>
                  <FundraisingStrategies />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/np/impact-products/government-funding" element={
              <ProtectedRoute>
                <AppLayout>
                  <GovernmentFunding />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/np/impact-products/information-security" element={
              <ProtectedRoute>
                <AppLayout>
                  <InformationSecurity />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/np/impact-products/operational-security" element={
              <ProtectedRoute>
                <AppLayout>
                  <OperationalSecurity />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/np/impact-products/resilience-playbook" element={
              <ProtectedRoute>
                <AppLayout>
                  <ResiliencePlaybook />
                </AppLayout>
              </ProtectedRoute>
            } />
              <Route path="/prv/retirement-calc-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RetirementCalcPlanner />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/prv/prv-prospecting" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PRVProspecting />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/prv/prv-ecosystem-wheel-generator" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PRVEcosystemWheelGenerator />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/prv/credit-assessment-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <CreditAssessmentPlanner />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/prv/budget-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <BudgetPlanner/>
                  </AppLayout>
                </ProtectedRoute>
              } />
            <Route path="/prv/understanding-taxes-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <UnderstandingTaxesPlanner />
                  </AppLayout>
                </ProtectedRoute>
              } />
            <Route path="/prv/food-truck-planner" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FoodTruckPlanner />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/prv/data-product-page" element={
                <ProtectedRoute>
                  <AppLayout>
                    <DataProductPage />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
            <Route path="/prv/food-truck" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FoodTruck />
                  </AppLayout>
                </ProtectedRoute>
              } />
            <Route path="/prv/food-truck-marketing" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FoodTruckMarketing />
                  </AppLayout>
                </ProtectedRoute>
              } />
            <Route path="/prv/food-truck-equipment" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FoodTruckEquipment />
                  </AppLayout>
                </ProtectedRoute>
              } />
            <Route path="/prv/food-truck-workers" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FoodTruckWorkers />
                  </AppLayout>
                </ProtectedRoute>
              } />
            <Route path="/prv/food-truck-equipment" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FoodTruckEquipment />
                  </AppLayout>
                </ProtectedRoute>
              } />
            <Route path="/prv/food-truck-insurance" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FoodTruckInsurance />
                  </AppLayout>
                </ProtectedRoute>
              } />
            <Route path="/prv/food-truck-startup-costs" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FoodTruckStartupCosts />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/prv/food-truck-bank-credit-accounts" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FoodTruckBankCreditAccounts />
                  </AppLayout>
                </ProtectedRoute>
              } />
          
            <Route path="/oh/oh-our-home" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OHOurHome />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/oh-home-asset-maintain-modeler" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OHOurHomeAssetMaintainModeler />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/oh-brokerage-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OHBrokerageDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/oh-car-asset-maintain-modeler" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OHOurCarAssetMaintainModeler />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/car-sales-profitability" element={
                <ProtectedRoute>
                  <AppLayout>
                    <CarSalesProfitability />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/rooftop-optimize" element={
                <ProtectedRoute>
                  <AppLayout>
                    <RooftopOptimize />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/oh-our-car" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OHOurCar />
                  </AppLayout>
                </ProtectedRoute>
              } />
            <Route path="/oh/oh-our-home-vendor-setup" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OHOurHomeVendorSetup />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/ohm-user-profile" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OhmUserProfile />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/ohm-user-profile-re" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OhmUserProfileRE />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/oh-our-car-vendor-setup" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OHOurCarVendorSetup />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/oh-vendor-subscribe" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OHVendorSubscribe />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/oh-real-estate-agent-subscribe" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OHREAgentSubscribe />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/oh-our-home-subscribe" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OHOurHomeSubscribe />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oh/oh-our-car-subscribe" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OHOurCarSubscribe />
                  </AppLayout>
                </ProtectedRoute>
              } />
            <Route path="/hoa/hoa-roi-estimator" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOAROIEstimator />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/hoa-our-home" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOAOurHome />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/our-home-vendor-mgmt" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OurHomeVendorMgmt />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/hoa-transform" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOATransform />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/hoa-membership" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOAMembership />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/hoa/haris" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HARIS />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/hoa-financials" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOAFinancials />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/referral-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ReferralDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/hoa-community-connect" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOACommunityConnect />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/hoa/hoa-finance-pulse" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOAFinancePulse />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/hoa-finance-data-manager" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOAFinanceDataManager />
                  </AppLayout>
                </ProtectedRoute>
              } />
              

              {/*Create a parent route if you want to conditionally show Pulse after DataManager is complete and data exists
              (better for sharing data)*/}
              {/*
              <Route 
                path="/hoa" 
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <FinanceDataProvider>
                        <Outlet />
                      </FinanceDataProvider>
                    </AppLayout>
                  </ProtectedRoute>
                }
              >
                <Route path="hoa-finance-pulse" element={<HOAFinancePulse />} />
                <Route path="hoa-finance-data-manager" element={
                  <HOAFinanceDataManager 
                    hoaId="123"
                    onComplete={() => Navigate('/hoa/hoa-finance-pulse')}
                    onCancel={() => Navigate('/hoa/hoa-finance-pulse')}
                  />
                } />
              </Route>
              */}
              <Route path="/hoa/hoa-asset-shield" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOAAssetShield />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/hoa-compliance-sync" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOAComplianceSync />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/premium-hoa-tuneup" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PremiumHOATuneup />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/premium-hoa-tuneup-data-manager" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PremiumHOATuneupDataManager />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/premium-hoa-tuneup-assessment" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PremiumHOATuneupAssessment />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/hoa/hoa-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOAFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/hoa-tuneup-flow-diagram" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOATuneupFlowDiagram />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/hoa/hoa-operating-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HOAOperatingDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/portfolio/historical-currency-rates" element={
                <ProtectedRoute>
                  <AppLayout>
                    <HistoricalCurrencyRates />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/mmg/mmg-exam" element={
                <ProtectedRoute>
                  <AppLayout>
                    <MMGExam />
                  </AppLayout>
                </ProtectedRoute>
              } />

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;

